/**
 * Pure post-submit view routing for the wizard (SPEC-SOR-79-v1).
 *
 * The server's three-way submit branch owns ServerOrderState (`draft` |
 * `submitted` | `submission-in-doubt`); the persisted order status can
 * additionally read `accepted` once the court has already accepted the
 * filing — for the submit flow both mean a server-confirmed successful
 * submit. `EFilingContext.submitOrderToApi` gates its reconcile decision on
 * these functions, and the spec property suite
 * (`internal/proptests/spec_sor_79_v1proptest/gen/`) imports them directly so
 * the routing invariants are pinned against product code, not a re-declared
 * model.
 */

/** Spec-level observed submit state derived from a persisted order status. */
export type ObservedSubmitState = 'submitted' | 'submission-in-doubt' | 'draft';

/** What the wizard renders for a submit attempt once the state is observed. */
export type PostSubmitView = 'confirmation' | 'in-doubt-pending' | 'error-with-retry';

/**
 * Maps a persisted order status to the spec-level ObservedSubmitState.
 * `accepted` is a submit the court has already accepted — observed
 * `submitted` for routing purposes. Any other status means the server holds
 * no confirmed submit for this attempt: observed `draft`.
 */
export function toObservedSubmitState(status: string): ObservedSubmitState {
  if (status === 'submitted' || status === 'accepted') return 'submitted';
  if (status === 'submission-in-doubt') return 'submission-in-doubt';
  return 'draft';
}

/** Routes an observed submit state to the view the wizard renders. */
export function routeSubmitView(state: ObservedSubmitState): PostSubmitView {
  if (state === 'submitted') return 'confirmation';
  if (state === 'submission-in-doubt') return 'in-doubt-pending';
  return 'error-with-retry';
}

/**
 * A status is settled when the submit attempt reached the server (confirmed
 * success or committed in-doubt) — i.e. the routed view is not
 * error-with-retry. The reconcile branch resolves on settled statuses and
 * only offers retry on a confirmed `draft`.
 */
export function isSettledSubmitStatus(status: string): boolean {
  return routeSubmitView(toObservedSubmitState(status)) !== 'error-with-retry';
}
